import { useRef, useCallback } from "react";
import isEqual from "lodash/isEqual";

// Tracks which fields differ from a baseline object, given individual
// key/value change reports from an external source (e.g. a non-React-owned
// form widget's own change events). Analogous to react-hook-form's
// `dirtyFields`, but driven by reported changes instead of owning inputs.
export function useDirtyFieldTracker(initialData) {
  const baselineRef = useRef(initialData ? JSON.parse(JSON.stringify(initialData)) : null);
  const dirtyFieldsRef = useRef({});

  const resetBaseline = useCallback((data) => {
    baselineRef.current = data ? JSON.parse(JSON.stringify(data)) : null;
    dirtyFieldsRef.current = {};
  }, []);

  const trackChange = useCallback((key, value) => {
    const baselineValue = baselineRef.current?.[key];
    const changed = !baselineRef.current || !isEqual(value, baselineValue);

    if (changed) {
      dirtyFieldsRef.current[key] = value;
    } else {
      delete dirtyFieldsRef.current[key];
    }
  }, []);

  const getDirtyFields = useCallback(() => ({ ...dirtyFieldsRef.current }), []);

  const clear = useCallback(() => {
    dirtyFieldsRef.current = {};
  }, []);

  return { resetBaseline, trackChange, getDirtyFields, clear };
}
